class TableStickyHeader extends HTMLElement {
  constructor() {
    super();
    this._tableWrapper = null;
    this._tableContainer = null;
    this._tableHeader = null;
    this._headerClone = null;
    this._secondaryNavSection = null;
    this._headerContainer = null;
    this._isCloneVisible = false;
    this._rafId = null;
  }

  connectedCallback() {
    this._findElements();
    this._createHeaderClone();
    this._setupListeners();
    this._scheduleUpdate();
  }

  disconnectedCallback() {
    if (this._scrollHandler) {
      window.removeEventListener("scroll", this._scrollHandler);
    }
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler);
    }
    if (this._wrapperScrollHandler && this._tableWrapper) {
      this._tableWrapper.removeEventListener("scroll", this._wrapperScrollHandler);
    }
    if (this._headerClone && this._headerClone.parentNode) {
      this._headerClone.parentNode.removeChild(this._headerClone);
    }
  }

  _findElements() {
    this._tableWrapper = this.querySelector(".table-wrapper");
    this._tableContainer = this.querySelector(".table-container");
    this._tableHeader = this.querySelector(".table-header");
    this._secondaryNavSection = document.querySelector(".shopify-section.section-secondary-navigation");
    this._headerContainer = document.querySelector(".header-container");
  }

  _createHeaderClone() {
    if (!this._tableHeader || this._headerClone) return;

    this._headerClone = this._tableHeader.cloneNode(true);
    this._headerClone.classList.add("table-header-clone");
    this._headerClone.setAttribute("aria-hidden", "true");
    this.appendChild(this._headerClone);
  }

  _setupListeners() {
    this._scrollHandler = () => {
      this._scheduleUpdate();
    };
    this._resizeHandler = this._throttle(() => {
      this._scheduleUpdate();
    }, 50);
    this._wrapperScrollHandler = () => {
      this._syncCloneScroll();
    };

    window.addEventListener("scroll", this._scrollHandler, { passive: true });
    window.addEventListener("resize", this._resizeHandler, { passive: true });

    if (this._tableWrapper) {
      this._tableWrapper.addEventListener("scroll", this._wrapperScrollHandler, { passive: true });
    }
  }

  _scheduleUpdate() {
    if (this._rafId) return;

    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      this._updateStickyHeader();
    });
  }

  _updateStickyHeader() {
    if (!this._tableWrapper || !this._tableContainer || !this._tableHeader || !this._headerClone) return;

    const wrapperRect = this._tableWrapper.getBoundingClientRect();
    const headerHeight = this._tableHeader.getBoundingClientRect().height;
    const stickyTopOffset = this._getStickyTopOffset(wrapperRect);
    const shouldShowClone =
      wrapperRect.top <= stickyTopOffset &&
      wrapperRect.bottom > stickyTopOffset + headerHeight;

    if (!shouldShowClone) {
      this._hideClone();
      return;
    }

    this._showClone(wrapperRect, stickyTopOffset, headerHeight);
  }

  _showClone(wrapperRect, stickyTopOffset, headerHeight) {
    if (!this._headerClone) return;

    this._headerClone.style.display = "flex";
    this._headerClone.style.position = "fixed";
    this._headerClone.style.top = `${stickyTopOffset}px`;
    this._headerClone.style.left = `${wrapperRect.left}px`;
    this._headerClone.style.width = `${wrapperRect.width}px`;
    this._headerClone.style.height = `${headerHeight}px`;
    this._headerClone.style.zIndex = "100";
    this._headerClone.style.backgroundColor = "#ffffff";
    this._headerClone.style.overflowX = "hidden";
    this._headerClone.style.overflowY = "hidden";
    this._headerClone.style.pointerEvents = "none";
    this._headerClone.style.boxSizing = "border-box";
    this._headerClone.style.overflowX = "auto";

    this.classList.add("is-header-stuck");
    this.style.setProperty("--table-sticky-header-height", `${headerHeight}px`);
    this._isCloneVisible = true;
    this._syncCloneScroll();
  }

  _hideClone() {
    if (!this._headerClone) return;

    this._headerClone.style.display = "none";
    this._headerClone.style.top = "";
    this._headerClone.style.left = "";
    this._headerClone.style.width = "";
    this._headerClone.style.height = "";
    this.classList.remove("is-header-stuck");
    this.style.removeProperty("--table-sticky-header-height");
    this._isCloneVisible = false;
  }

  _syncCloneScroll() {
    if (!this._isCloneVisible || !this._headerClone || !this._tableWrapper) return;
    this._headerClone.scrollLeft = this._tableWrapper.scrollLeft;
  }

  _getStickyTopOffset(wrapperRect) {
    const headerHeight = this._getVisibleHeaderHeight();

    if (!this._secondaryNavSection) {
      return headerHeight;
    }

    const navRect = this._secondaryNavSection.getBoundingClientRect();
    const navHeight = this._secondaryNavSection.offsetHeight || 0;
    const navIsPinned =
      navRect.bottom > 0 && navRect.top <= headerHeight + 1 && !!wrapperRect && wrapperRect.top <= navRect.bottom;

    return Math.max(0, navIsPinned ? headerHeight + navHeight : headerHeight);
  }

  _getVisibleHeaderHeight() {
    if (!this._headerContainer) return 0;

    const isHidden =
      this._headerContainer.classList.contains("header-container--hidden") ||
      this._headerContainer.style.transform === "translateY(-100%)";

    return isHidden ? 0 : this._headerContainer.offsetHeight || 0;
  }

  _throttle(func, limit) {
    let inThrottle;

    return (...args) => {
      if (inThrottle) return;
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    };
  }
}

if (!window.customElements.get("table-sticky-header")) {
  window.customElements.define("table-sticky-header", TableStickyHeader);
}
